import React, { useState } from "react";
import { FileText, Search, ExternalLink, FileSpreadsheet, FileImage, Presentation, File } from "lucide-react";
import { useApi, fetchDocuments } from "../api";
import { PageHeader, Loading, ErrorState, formatDate } from "../components/common";
import { Input } from "../components/ui/input";

const typeMeta = {
  PDF: { icon: FileText, color: "#ef4444", bg: "#fef2f2" },
  XLSX: { icon: FileSpreadsheet, color: "#10b981", bg: "#ecfdf5" },
  CSV: { icon: FileSpreadsheet, color: "#059669", bg: "#ecfdf5" },
  DOCX: { icon: FileText, color: "#D0333A", bg: "#eff6ff" },
  AI: { icon: FileImage, color: "#f59e0b", bg: "#fffbeb" },
  PPTX: { icon: Presentation, color: "#f97316", bg: "#fff7ed" },
};

export default function Documents() {
  const [q, setQ] = useState("");
  const { data: documents, loading, error, reload } = useApi(fetchDocuments);
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading || !documents) return <Loading />;

  const list = documents.filter((d) => (d.name + d.project + d.category).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="mx-auto max-w-7xl animate-fade-in-up">
      <PageHeader title="Documents" subtitle={`${documents.length} live document${documents.length !== 1 ? "s" : ""}`} />
      <div className="mb-4 relative max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents..." className="h-9 border-slate-200 bg-white pl-9 text-sm" />
      </div>
      {list.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-400">No documents found.</div>
      ) : (
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Project</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Added</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((d) => {
                const meta = typeMeta[d.type] || { icon: File, color: "#64748b", bg: "#f1f5f9" };
                const Icon = meta.icon;
                return (
                  <tr key={d.id} className="transition-colors hover:bg-slate-50">
                    <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: meta.bg }}><Icon className="h-4 w-4" style={{ color: meta.color }} /></div><div><p className="font-medium text-slate-800">{d.name}</p>{d.description && <p className="text-xs text-slate-400">{d.description}</p>}</div></div></td>
                    <td className="px-4 py-3 text-slate-600">{d.project || "—"}</td>
                    <td className="px-4 py-3"><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{d.category}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500">{formatDate(d.createdDate)}</td>
                    <td className="px-4 py-3">{d.url ? <a href={d.url} target="_blank" rel="noreferrer" className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-red-600 inline-flex"><ExternalLink className="h-4 w-4" /></a> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}
